import {
  type IAdminCategory,
  type ILanguageType,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Col, Divider, Form, Input, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AddFocusEventListener,
  FORM_TEXT_HOTKEYS,
  FormTextHandler,
} from '../../fragments/form-events';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { EHotkey, useHotkey } from '../../hooks/use-hotkey';
import { nameLive, nameUpdate } from '../../reducers/category/reducer';
import FormSubmitFooter from '../form-submit-footer';
import GoogleTranslate from '../google-translate';
import GoogleTranslateAutomat, {
  type ITextTranslate,
} from '../google-translate-automat';

interface IForm {
  name: string;
}

const getDirectTranslation = (
  translations: ITranslation[] | undefined,
  lang?: ILanguageType,
): string | null =>
  translations?.find(({ code }: ITranslation): boolean => code === lang?.code)
    ?.value ?? null;

function NameForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const category = useSelector((s: RootState) => s.Category.category);
  const categoryInProcess = useSelector((s: RootState) => s.Category.inProcess);
  const categoryUpdatedAt = useSelector((s: RootState) => s.Category.updatedAt);

  const [form] = Form.useForm<IForm>();
  const [categoryNameEN, setCategoryNameEN] = useState<ITranslation>();
  const [gtLinkName, setGtLinkName] = useState<string>('GT name EN → ---');

  const categoryNameTranslated = useMemo(
    () => getDirectTranslation(category?.names, languageType) ?? '',
    [category?.names, languageType, categoryUpdatedAt],
  );

  useEffect((): void => {
    const productNameENUE: ITranslation | undefined = category?.names.find(
      (p: ITranslation): boolean => p.code === 'EN',
    );
    setCategoryNameEN(productNameENUE);
    setGtLinkName(`GT name EN → ${languageType?.code}`);

    form.setFieldsValue({ name: categoryNameTranslated });
  }, [languageType?.code, category?.names, categoryNameTranslated, form]);

  const translateName = useCallback((): void => {
    AddFocusEventListener(form, (text: string): void => {
      form.setFieldValue('name', text);

      if (!languageType) {
        return;
      }

      dispatch(nameLive(text, languageType.code));
    });
  }, [languageType?.code, dispatch, form]);

  useHotkey(FORM_TEXT_HOTKEYS, (hotkey: EHotkey): void => {
    FormTextHandler(form, hotkey, (text: string): void => {
      form.setFieldValue('name', text);

      if (!languageType) {
        return;
      }

      dispatch(nameLive(text, languageType.code));
    });
  });

  const onFinish = useCallback(
    ({ name }: IForm, cat: IAdminCategory | undefined): void => {
      if (cat && languageType) {
        dispatch(nameUpdate(cat.uuid, name, languageType.code));
      }
    },
    [languageType?.code, dispatch],
  );

  const categoryNameENStr: string = categoryNameEN?.value || '';

  const texts: ITextTranslate[] = [
    {
      text: categoryNameENStr,
      callbacks: [
        (text: string, lc: string): void => {
          form.setFieldValue('name', text);
          dispatch(nameLive(text, lc));
        },
      ],
    },
  ];

  return (
    <Form
      form={form}
      onFinish={(values: IForm): void | null | undefined =>
        category && onFinish(values, category)
      }
      layout="vertical"
    >
      <Row>
        <Col span={3}>
          <FormSubmitFooter
            inProcess={categoryInProcess}
            updatedAt={categoryUpdatedAt}
          />
        </Col>

        <Col span={3}>
          <GoogleTranslateAutomat
            texts={texts}
            submit={form.submit}
            isSaving={categoryInProcess}
          />
        </Col>

        <Col span={3}>
          <GoogleTranslate
            fromLanguageCode="EN"
            toLanguageCode={languageType?.code}
            name={gtLinkName}
            text={categoryNameENStr}
            onClick={(): void => translateName()}
          />
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
              },
              {
                type: 'string',
                min: 2,
              },
            ]}
          >
            <Input
              placeholder="Name"
              disabled={categoryInProcess}
              onBlur={(e): void => {
                if (!languageType) {
                  return;
                }

                dispatch(nameLive(e.target.value.trim(), languageType.code));
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

function Name(): React.ReactElement {
  return <NameForm />;
}

export default Name;
